import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiGetTicket, apiReplyToTicket } from '../services/api';
import {
  TICKET_CATEGORY_LABELS,
  TICKET_STATUS_LABELS,
  formatTicketDate,
  isTicketClosed,
} from '../constants/support';
import './Legal.css';
import './SupportTicket.css';

/**
 * Fil d'un ticket de support, côté utilisateur.
 *
 * L'admin lit et répond depuis son dashboard : cette page n'est jamais la sienne,
 * d'où l'absence de contrôle de statut ici — seul le backend l'autorise.
 */
const SupportTicket = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [ticket, setTicket] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    apiGetTicket(id, getToken())
      .then(setTicket)
      .catch((err) => setError(err.message));
  }, [id, getToken]);

  useEffect(() => { load(); }, [load]);

  const handleReply = async (e) => {
    e.preventDefault();
    setError('');
    setSending(true);
    try {
      setTicket(await apiReplyToTicket(id, reply.trim(), getToken()));
      setReply('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const closed = ticket && isTicketClosed(ticket.statut);

  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <button className="legal-page__back-btn" onClick={() => navigate('/legal/contact')}>
          <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <h1 className="legal-page__title">Ma demande</h1>
      </div>

      {error && <p className="legal-page__form-error">{error}</p>}

      {ticket && (
        <>
          <div className="legal-page__card">
            <div className="legal-page__section">
              <h2 className="legal-page__section-title">{ticket.sujet}</h2>
              <p className="legal-page__contact-sublabel">
                {TICKET_CATEGORY_LABELS[ticket.categorie] || ticket.categorie}
                {' · '}
                {TICKET_STATUS_LABELS[ticket.statut] || ticket.statut}
                {' · ouverte le '}
                {formatTicketDate(ticket.created_at)}
              </p>
            </div>
          </div>

          <div className="legal-page__card">
            <div className="support-thread">
              {ticket.messages.map((m) => (
                <div
                  key={m.id}
                  className={`support-thread__msg support-thread__msg--${m.from_admin ? 'admin' : 'me'}`}
                >
                  <p className="support-thread__author">
                    {m.from_admin ? 'CycleGala' : 'Vous'}
                    {!m.auteur_actif && ' (compte supprimé)'}
                  </p>
                  <p className="support-thread__body">{m.message}</p>
                  <p className="support-thread__date">{formatTicketDate(m.created_at)}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="legal-page__card">
            <div className="legal-page__section">
              {closed ? (
                <p className="legal-page__section-text">
                  Cette demande est close. Ouvrez-en une nouvelle depuis la page
                  Contact&nbsp;&amp;&nbsp;Support si vous avez besoin de nous réécrire.
                </p>
              ) : (
                <form className="legal-page__form" onSubmit={handleReply}>
                  <label className="legal-page__form-label" htmlFor="support-reply">
                    Répondre
                  </label>
                  <textarea
                    id="support-reply"
                    className="legal-page__form-textarea"
                    rows={4}
                    value={reply}
                    required
                    maxLength={5000}
                    onChange={(e) => setReply(e.target.value)}
                  />
                  <button className="legal-page__form-submit" type="submit" disabled={sending}>
                    {sending ? 'Envoi…' : 'Envoyer'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SupportTicket;
